import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import SidebarLinkGroup from './SidebarLinkGroup';
import Logo from '../../images/logo/logo-light.svg';
import {
    AccordionToggleSvg,
    DashboardSvg,
    CampaignSvg,
    MessagesSvg,
    SearchSvg,
    MedicalSvg,
    MedCentersSvg,
    OrganizationsSvg,
    MoneySvg,
    SyncronizeSvg,
    PiggyBankSvg,
    GrantsSvg,
    FilesSvg,
    SettingsSvg,
    UsersSvg,
} from '../../svg';
import { SidebarLink } from './SidebarLink';
import { useAuth } from '~/context/AuthProvider';
import { StatusSidebarLink } from './StatusSidebarLink';
import { useWebhookErrorUnreadCount } from '~/hooks/useWebhookErrorUnreadCount';

interface SidebarProps {
    sidebarOpen: boolean;
    setSidebarOpen: (arg: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
    const { role, user } = useAuth();
    // The Webhook Errors link (and its badge) is hidden from medical professionals,
    // so don't poll the count for them either.
    const { count: webhookErrorUnreadCount } = useWebhookErrorUnreadCount({
        enabled: role !== 'medical professional',
    });
    const location = useLocation();
    const { pathname } = location;

    const trigger = useRef<any>(null);
    const sidebar = useRef<any>(null);

    const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
    const [sidebarExpanded, setSidebarExpanded] = useState(
        storedSidebarExpanded === null
            ? false
            : storedSidebarExpanded === 'true',
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const [status, setStatus] = useState<string | null>(null);
    // Every Files child points at /files, so pathname matching lit all four
    // at once. The document type in the query string is what distinguishes
    // them, the same way `status` does for the fund request links.
    const docType = searchParams.get('type');

    // close on click outside
    useEffect(() => {
        const clickHandler = ({ target }: MouseEvent) => {
            if (!sidebar.current || !trigger.current) return;
            if (
                !sidebarOpen ||
                sidebar.current.contains(target) ||
                trigger.current.contains(target)
            )
                return;
            setSidebarOpen(false);
        };
        document.addEventListener('click', clickHandler);
        return () => document.removeEventListener('click', clickHandler);
    });

    // close if the esc key is pressed
    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (!sidebarOpen || keyCode !== 27) return;
            setSidebarOpen(false);
        };
        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    });

    useEffect(() => {
        localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
        if (sidebarExpanded) {
            document.querySelector('body')?.classList.add('sidebar-expanded');
        } else {
            document
                .querySelector('body')
                ?.classList.remove('sidebar-expanded');
        }
    }, [sidebarExpanded]);

    useEffect(() => {
        // The only searchParams that should be updated are the status.
        // On status changes, reset the filters
        const fundrequestStatus = searchParams.get('status');
        if (fundrequestStatus) {
            setStatus(fundrequestStatus);
        }
    }, [searchParams]);

    // Check if user has administrator role
    const isAdministrator = user?.roles?.includes('administrator');

    // Check if user has finance access (requires both 'staff' and 'Financial' roles)
    const hasFinanceAccess =
        user?.roles?.includes('staff') && user?.roles?.includes('Financial');

    return (
        <aside
            ref={sidebar}
            className={`absolute left-0 top-0 z-9999 flex h-screen w-70 flex-col overflow-y-hidden bg-black duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0 ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
            {/* <!-- SIDEBAR HEADER --> */}
            <div className='flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5'>
                <NavLink to='https://helphopelive.org'>
                    <img src={Logo} alt='Logo' />
                </NavLink>

                <button
                    ref={trigger}
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    aria-controls='sidebar'
                    aria-expanded={sidebarOpen}
                    className='block lg:hidden'
                >
                    <svg
                        className='fill-current'
                        width='20'
                        height='18'
                        viewBox='0 0 20 18'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path
                            d='M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z'
                            fill=''
                        />
                    </svg>
                </button>
            </div>
            {/* <!-- SIDEBAR HEADER --> */}

            <div className='no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear'>
                {/* <!-- Sidebar Menu --> */}
                <nav className='px-4 py-4 text-white lg:px-6'>
                    {/* <!-- Menu Group --> */}
                    <div>
                        <ul className='mb-6 flex flex-col gap-1.5'>
                            <li>
                                <NavLink
                                    to='/'
                                    className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                        pathname === '/' &&
                                        'bg-white text-primary dark:bg-meta-4'
                                    }`}
                                >
                                    <DashboardSvg />
                                    My Dashboard
                                </NavLink>
                            </li>

                            {role !== 'medical professional' && (
                                <>
                                    {/* <!-- Menu Item Campaigns --> */}
                                    <SidebarLinkGroup
                                        activeCondition={
                                            (pathname === '/campaigns' ||
                                                pathname.includes('campaign') ||
                                                pathname.includes('changes')) &&
                                            !pathname.startsWith('/search')
                                        }
                                    >
                                        {(handleClick, open) => {
                                            return (
                                                <React.Fragment>
                                                    <NavLink
                                                        to='#'
                                                        className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                            (pathname ===
                                                                '/campaigns' ||
                                                                pathname.includes(
                                                                    'campaign',
                                                                ) ||
                                                                pathname.includes(
                                                                    'changes',
                                                                )) &&
                                                            !pathname.startsWith(
                                                                '/search',
                                                            ) &&
                                                            'bg-white text-primary dark:bg-meta-4'
                                                        }`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            sidebarExpanded
                                                                ? handleClick()
                                                                : setSidebarExpanded(
                                                                      true,
                                                                  );
                                                        }}
                                                    >
                                                        <CampaignSvg />
                                                        Campaigns
                                                        <AccordionToggleSvg
                                                            open={open}
                                                        />
                                                    </NavLink>
                                                    {/* <!-- Dropdown Menu Start --> */}
                                                    <div
                                                        className={`translate transform overflow-hidden ${
                                                            !open && 'hidden'
                                                        }`}
                                                    >
                                                        <ul className='mb-5.5 mt-4 flex flex-col gap-2.5 pl-7'>
                                                            <li>
                                                                <SidebarLink
                                                                    to='/client-campaigns'
                                                                    label='Client Campaigns'
                                                                    activePattern={
                                                                        /^\/campaigns\/\d+/
                                                                    }
                                                                />
                                                            </li>
                                                            <li>
                                                                <SidebarLink
                                                                    to='/campaign-applications'
                                                                    label='Campaign Applications'
                                                                />
                                                            </li>
                                                            <li>
                                                                <SidebarLink
                                                                    to='/campaign-requests'
                                                                    label='Campaign Requests'
                                                                />
                                                            </li>
                                                            <li>
                                                                <SidebarLink
                                                                    to='/non-client-campaigns'
                                                                    label='Non-Client Campaigns'
                                                                />
                                                            </li>
                                                            <li>
                                                                <SidebarLink
                                                                    to='/new-campaigns'
                                                                    label='Newly Opened Campaigns'
                                                                />
                                                            </li>
                                                            {/* <li>
                                                                <SidebarLink
                                                                    to='/campaign-notifications'
                                                                    label='Campaign Notifications'
                                                                />
                                                            </li> */}
                                                            <li>
                                                                <SidebarLink
                                                                    to='/un-monitored-changes'
                                                                    label='Un-Monitored Changes'
                                                                />
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    {/* <!-- Dropdown Menu End --> */}
                                                </React.Fragment>
                                            );
                                        }}
                                    </SidebarLinkGroup>

                                    {/* <!-- Menu Item Reports --> */}
                                    <SidebarLinkGroup
                                        activeCondition={
                                            pathname === '/reports' ||
                                            pathname.includes('report')
                                        }
                                    >
                                        {(handleClick, open) => {
                                            return (
                                                <React.Fragment>
                                                    <NavLink
                                                        to='#'
                                                        className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                            (pathname ===
                                                                '/reports' ||
                                                                pathname.includes(
                                                                    'report',
                                                                )) &&
                                                            'bg-white text-primary dark:bg-meta-4'
                                                        }`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            sidebarExpanded
                                                                ? handleClick()
                                                                : setSidebarExpanded(
                                                                      true,
                                                                  );
                                                        }}
                                                    >
                                                        <SearchSvg />
                                                        Reports
                                                        <AccordionToggleSvg
                                                            open={open}
                                                        />
                                                    </NavLink>
                                                    {/* <!-- Dropdown Menu Start --> */}
                                                    <div
                                                        className={`translate transform overflow-hidden ${
                                                            !open && 'hidden'
                                                        }`}
                                                    >
                                                        <ul className='mb-5.5 mt-4 flex flex-col gap-2.5 pl-7'>
                                                            <li>
                                                                <SidebarLink
                                                                    to='/new-report'
                                                                    label='New Report'
                                                                />
                                                            </li>
                                                            <li>
                                                                <SidebarLink
                                                                    to='/saved-reports'
                                                                    label='Saved Reports'
                                                                />
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    {/* <!-- Dropdown Menu End --> */}
                                                </React.Fragment>
                                            );
                                        }}
                                    </SidebarLinkGroup>
                                </>
                            )}

                            {role !== 'medical professional' && (
                                <li>
                                    <NavLink
                                        to='/messages'
                                        className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                            pathname === '/messages' &&
                                            'bg-white text-primary dark:bg-meta-4'
                                        }`}
                                    >
                                        <MessagesSvg />
                                        Messages
                                    </NavLink>
                                </li>
                            )}

                            {role !== 'medical professional' && (
                                <>
                                    <li>
                                        <div className='mb-3 mt-8 pl-4 text-sm uppercase text-secondary'>
                                            Outreach
                                        </div>
                                    </li>

                                    <li>
                                        <NavLink
                                            to='/medical-professionals'
                                            className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                pathname?.includes(
                                                    '/medical-professionals',
                                                ) &&
                                                'bg-white text-primary dark:bg-meta-4'
                                            }`}
                                        >
                                            <MedicalSvg />
                                            Medical Professionals
                                        </NavLink>
                                    </li>

                                    <li>
                                        <NavLink
                                            to='/medical-centers'
                                            className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                pathname?.includes(
                                                    '/medical-centers',
                                                ) &&
                                                'bg-white text-primary dark:bg-meta-4'
                                            }`}
                                        >
                                            <MedCentersSvg />
                                            Med Centers
                                        </NavLink>
                                    </li>

                                    <li>
                                        <NavLink
                                            to='/vendors-organizations'
                                            className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                pathname?.includes(
                                                    '/vendors-organizations',
                                                ) &&
                                                'bg-white text-primary dark:bg-meta-4'
                                            }`}
                                        >
                                            <OrganizationsSvg />
                                            Vendors &amp; Organizations
                                        </NavLink>
                                    </li>

                                    {hasFinanceAccess && (
                                        <>
                                            <li>
                                                <div className='mb-3 mt-8 pl-4 text-sm uppercase text-secondary'>
                                                    Finance
                                                </div>
                                            </li>

                                            <SidebarLinkGroup
                                                activeCondition={
                                                    pathname ===
                                                        '/fund-requests' ||
                                                    pathname.includes(
                                                        'fund-requests',
                                                    )
                                                }
                                            >
                                                {(handleClick, open) => {
                                                    return (
                                                        <React.Fragment>
                                                            <NavLink
                                                                to='#'
                                                                className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                                    (pathname ===
                                                                        '/fund-requests' ||
                                                                        pathname.includes(
                                                                            'fund-requests',
                                                                        )) &&
                                                                    'bg-white text-primary dark:bg-meta-4'
                                                                }`}
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.preventDefault();
                                                                    sidebarExpanded
                                                                        ? handleClick()
                                                                        : setSidebarExpanded(
                                                                              true,
                                                                          );
                                                                }}
                                                            >
                                                                <MoneySvg />
                                                                Requests
                                                                <AccordionToggleSvg
                                                                    open={open}
                                                                />
                                                            </NavLink>
                                                            {/* <!-- Dropdown Menu Start --> */}
                                                            <div
                                                                className={`translate transform overflow-hidden  ${
                                                                    !open &&
                                                                    'hidden'
                                                                }`}
                                                            >
                                                                <ul className='mb-5.5 mt-4 flex flex-col gap-2.5 pl-7'>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=open'
                                                                            label='All Open'
                                                                            isActive={
                                                                                status ===
                                                                                'open'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=submitted'
                                                                            label='Submitted'
                                                                            isActive={
                                                                                status ===
                                                                                'submitted'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=pending'
                                                                            label='Pending'
                                                                            isActive={
                                                                                status ===
                                                                                'pending'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=resubmitted'
                                                                            label='Resubmitted'
                                                                            isActive={
                                                                                status ===
                                                                                'resubmitted'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=in_review'
                                                                            label='In Review'
                                                                            isActive={
                                                                                status ===
                                                                                'in-review'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=ap_review'
                                                                            label='AP Review'
                                                                            isActive={
                                                                                status ===
                                                                                'ap-review'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=for_dof_approval'
                                                                            label='For DoF Approval'
                                                                            isActive={
                                                                                status ===
                                                                                'for-dof-approval'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=approved'
                                                                            label='Approved'
                                                                            isActive={
                                                                                status ===
                                                                                'approved'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=in_mip'
                                                                            label='In MIP'
                                                                            isActive={
                                                                                status ===
                                                                                'in_mip'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=appealed'
                                                                            label='Appealed'
                                                                            isActive={
                                                                                status ===
                                                                                'appealed'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=paid'
                                                                            label='Paid'
                                                                            isActive={
                                                                                status ===
                                                                                'paid'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=rejected'
                                                                            label='Rejected'
                                                                            isActive={
                                                                                status ===
                                                                                'rejected'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/fund-requests?status=admin_draft'
                                                                            label='Admin Draft'
                                                                            isActive={
                                                                                status ===
                                                                                'admin_draft'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/payees/request-changes'
                                                                            label='Vendor Change Requests'
                                                                        />
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            {/* <!-- Dropdown Menu End --> */}
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </SidebarLinkGroup>

                                            {/* <li>
                    <NavLink
                      to='/requests'
                      className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                        pathname === '/requests' &&
                        'bg-white text-primary dark:bg-meta-4'
                      }`}
                    >
                      <CampaignSvg />
                      Expenses
                    </NavLink>
                  </li> */}

                                            {/* <!-- Menu Item Synchronize Data --> */}
                                            <SidebarLinkGroup
                                                activeCondition={
                                                    pathname ===
                                                        '/synchronize-data' ||
                                                    pathname === '/payouts' ||
                                                    pathname.includes(
                                                        'synchronize',
                                                    )
                                                }
                                            >
                                                {(handleClick, open) => {
                                                    return (
                                                        <React.Fragment>
                                                            <NavLink
                                                                to='#'
                                                                className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                                    (pathname ===
                                                                        '/synchronize-data' ||
                                                                        pathname ===
                                                                            '/payouts') &&
                                                                    'bg-white text-primary dark:bg-meta-4'
                                                                }`}
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.preventDefault();
                                                                    sidebarExpanded
                                                                        ? handleClick()
                                                                        : setSidebarExpanded(
                                                                              true,
                                                                          );
                                                                }}
                                                            >
                                                                <SyncronizeSvg />
                                                                Synchronize Data
                                                                <AccordionToggleSvg
                                                                    open={open}
                                                                />
                                                            </NavLink>
                                                            {/* <!-- Dropdown Menu Start --> */}
                                                            <div
                                                                className={`translate transform overflow-hidden ${
                                                                    !open &&
                                                                    'hidden'
                                                                }`}
                                                            >
                                                                <ul className='mb-5.5 mt-4 flex flex-col gap-2.5 pl-7'>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/synchronize-data'
                                                                            label='Campaign Sync'
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/payouts'
                                                                            label='Payouts'
                                                                        />
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            {/* <!-- Dropdown Menu End --> */}
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </SidebarLinkGroup>

                                            {/* <!-- Menu Item Contributions --> */}
                                            <li>
                                                <NavLink
                                                    to='/one-time-contributions'
                                                    className={`hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                        pathname.includes(
                                                            'contributions',
                                                        ) &&
                                                        'bg-white text-primary dark:bg-meta-4'
                                                    }`}
                                                >
                                                    <PiggyBankSvg />
                                                    Contributions
                                                </NavLink>
                                            </li>

                                            {/* <!-- Menu Item Grants --> */}
                                            <SidebarLinkGroup
                                                activeCondition={
                                                    pathname ===
                                                        '/applied-grants' ||
                                                    pathname.includes('grant')
                                                }
                                            >
                                                {(handleClick, open) => {
                                                    return (
                                                        <React.Fragment>
                                                            <NavLink
                                                                to='#'
                                                                className={`group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                                    (pathname ===
                                                                        '/applied-grants' ||
                                                                        pathname.includes(
                                                                            'grant',
                                                                        )) &&
                                                                    'bg-white text-primary dark:bg-meta-4'
                                                                }`}
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.preventDefault();
                                                                    sidebarExpanded
                                                                        ? handleClick()
                                                                        : setSidebarExpanded(
                                                                              true,
                                                                          );
                                                                }}
                                                            >
                                                                <GrantsSvg />
                                                                Grants
                                                                <AccordionToggleSvg
                                                                    open={open}
                                                                />
                                                            </NavLink>
                                                            {/* <!-- Dropdown Menu Start --> */}
                                                            <div
                                                                className={`translate transform overflow-hidden ${
                                                                    !open &&
                                                                    'hidden'
                                                                }`}
                                                            >
                                                                <ul className='mb-5.5 mt-4 flex flex-col gap-2.5 pl-7'>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/applied-grants'
                                                                            label='Applied Grants'
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/grant-settings'
                                                                            label='Grant Settings'
                                                                        />
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            {/* <!-- Dropdown Menu End --> */}
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </SidebarLinkGroup>
                                        </>
                                    )}

                                    {isAdministrator && (
                                        <>
                                            <li>
                                                <div className='mb-3 mt-8 pl-4 text-sm uppercase text-secondary'>
                                                    Admin
                                                </div>
                                            </li>

                                            {/* <!-- Menu Item Files --> */}
                                            <SidebarLinkGroup
                                                activeCondition={
                                                    pathname === '/files' ||
                                                    pathname.includes('files')
                                                }
                                            >
                                                {(handleClick, open) => {
                                                    return (
                                                        <React.Fragment>
                                                            <NavLink
                                                                to='#'
                                                                className={`group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                                    (pathname ===
                                                                        '/files' ||
                                                                        pathname.includes(
                                                                            'files',
                                                                        )) &&
                                                                    'bg-white text-primary dark:bg-meta-4'
                                                                }`}
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.preventDefault();
                                                                    sidebarExpanded
                                                                        ? handleClick()
                                                                        : setSidebarExpanded(
                                                                              true,
                                                                          );
                                                                }}
                                                            >
                                                                <FilesSvg />
                                                                Files
                                                                <AccordionToggleSvg
                                                                    open={open}
                                                                />
                                                            </NavLink>
                                                            {/* <!-- Dropdown Menu Start --> */}
                                                            <div
                                                                className={`translate transform overflow-hidden ${
                                                                    !open &&
                                                                    'hidden'
                                                                }`}
                                                            >
                                                                <ul className='mb-5.5 mt-4 flex flex-col gap-2.5 pl-7'>
                                                                    {/*
                                                                        Documents (HHLTB-2808). The
                                                                        /files page existed as a
                                                                        design shell with no route
                                                                        into it; adding and editing
                                                                        these flyers, handouts,
                                                                        forms and letters meant
                                                                        logging into legacy. Each
                                                                        item opens the page on its
                                                                        own tab.
                                                                    */}
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/files?type=Flyer'
                                                                            label='Flyers'
                                                                            isActive={
                                                                                pathname ===
                                                                                    '/files' &&
                                                                                (docType ||
                                                                                    'Flyer') ===
                                                                                    'Flyer'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/files?type=Handout'
                                                                            label='Handouts'
                                                                            isActive={
                                                                                pathname ===
                                                                                    '/files' &&
                                                                                (docType ||
                                                                                    'Flyer') ===
                                                                                    'Handout'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/files?type=Form'
                                                                            label='Forms'
                                                                            isActive={
                                                                                pathname ===
                                                                                    '/files' &&
                                                                                (docType ||
                                                                                    'Flyer') ===
                                                                                    'Form'
                                                                            }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <StatusSidebarLink
                                                                            to='/files?type=Letter'
                                                                            label='Letters'
                                                                            isActive={
                                                                                pathname ===
                                                                                    '/files' &&
                                                                                (docType ||
                                                                                    'Flyer') ===
                                                                                    'Letter'
                                                                            }
                                                                        />
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            {/* <!-- Dropdown Menu End --> */}
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </SidebarLinkGroup>

                                            {/* <!-- Menu Item Settings --> */}
                                            <SidebarLinkGroup
                                                activeCondition={
                                                    pathname === '/settings' ||
                                                    pathname.includes(
                                                        'settings',
                                                    )
                                                }
                                            >
                                                {(handleClick, open) => {
                                                    return (
                                                        <React.Fragment>
                                                            <NavLink
                                                                to='#'
                                                                className={`group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                                    (pathname ===
                                                                        '/settings' ||
                                                                        pathname.includes(
                                                                            'settings',
                                                                        )) &&
                                                                    'bg-white text-primary dark:bg-meta-4'
                                                                }`}
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.preventDefault();
                                                                    sidebarExpanded
                                                                        ? handleClick()
                                                                        : setSidebarExpanded(
                                                                              true,
                                                                          );
                                                                }}
                                                            >
                                                                <SettingsSvg />
                                                                Settings
                                                                <AccordionToggleSvg
                                                                    open={open}
                                                                />
                                                            </NavLink>
                                                            {/* <!-- Dropdown Menu Start --> */}
                                                            <div
                                                                className={`translate transform overflow-hidden ${
                                                                    !open &&
                                                                    'hidden'
                                                                }`}
                                                            >
                                                                <ul className='mb-5.5 mt-4 flex flex-col gap-2.5 pl-7'>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/campaign-settings'
                                                                            label='Campaign Settings'
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/campaign-message-settings'
                                                                            label='Campaign Message'
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/registration-settings'
                                                                            label='Registration Settings'
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/medical-settings'
                                                                            label='Medical Settings'
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/financial-settings'
                                                                            label='Financial Settings'
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <SidebarLink
                                                                            to='/manage-terms-agreements'
                                                                            label='Manage Terms and Agreements'
                                                                        />
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            {/* <!-- Dropdown Menu End --> */}
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </SidebarLinkGroup>

                                            {/*
                                                <!-- Menu Item Users -->

                                                This was an accordion over six
                                                links, five of which went
                                                nowhere. Add User, Manage
                                                Departments, Manage Roles and
                                                Manage Permissions have no route
                                                at all, so they opened a blank
                                                page; they stay hidden until
                                                they're built. My Staff was
                                                All Users under another name,
                                                clients and all, and is now the
                                                "Staff only" checkbox on the
                                                Users list (HHLTB-2665). With
                                                one destination left, the
                                                accordion is just a click in the
                                                way.
                                            */}
                                            <li>
                                                <NavLink
                                                    to='/users'
                                                    className={({ isActive }) =>
                                                        `group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                            isActive
                                                                ? 'bg-white text-primary dark:bg-meta-4'
                                                                : ''
                                                        }`
                                                    }
                                                >
                                                    <UsersSvg />
                                                    Users
                                                </NavLink>
                                            </li>

                                            {/* <!-- Menu Item Webhook Errors --> */}
                                            <li>
                                                <NavLink
                                                    to='/webhook-errors'
                                                    className={({ isActive }) =>
                                                        `hover:bg-dark black:hover:bg-meta-4 group relative flex items-center gap-2.5 rounded-full px-4 py-2 font-bold duration-300 ease-in-out hover:bg-white hover:text-black dark:hover:bg-meta-4 ${
                                                            isActive
                                                                ? 'bg-white text-primary dark:bg-meta-4'
                                                                : ''
                                                        }`
                                                    }
                                                >
                                                    <SyncronizeSvg />
                                                    Webhook Errors
                                                    {webhookErrorUnreadCount >
                                                        0 && (
                                                        <span className='ml-auto inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-semibold'>
                                                            {
                                                                webhookErrorUnreadCount
                                                            }
                                                        </span>
                                                    )}
                                                </NavLink>
                                            </li>
                                        </>
                                    )}
                                </>
                            )}
                        </ul>
                    </div>
                </nav>
                {/* <!-- Sidebar Menu --> */}
            </div>
        </aside>
    );
};

export default Sidebar;
